// document-uploader.jsx
import { useState, useRef, useEffect } from "react";
import { Camera, Upload, FileImage, X, Smartphone, Clock } from "lucide-react";
import QRCodeGenerator from "./qr-code-generator";
import { useDocumentSync } from "./use-document-sync";

export default function DocumentUploader({
    documentType,
    sessionId,
    isMobileOrTablet,
    onUpload,
    allowMultiple = false,
    description,
    uploadedDocuments = [],
    removeDocument
}) {
    const fileInputRef = useRef(null);
    const cameraInputRef = useRef(null);

    const handleFileUpload = (files, device) => {
        if (!files) return;

        const fileArray = Array.from(files);
        fileArray.forEach((file) => {
            onUpload(file, documentType);
        });
    };

    const handleCameraCapture = () => {
        if (cameraInputRef.current) {
            cameraInputRef.current.click();
        }
    };

    const handleFileSelect = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const removeFile = (id) => {
        removeDocument(id)
    };

    const uploadUrl = `${typeof window !== "undefined" ? window.location.origin : ""
        }/upload/${sessionId}/${encodeURIComponent(documentType)}`;


    const [localDocuments, setLocalDocuments] = useState([]);

    useEffect(() => {
        const enrichDocuments = async () => {
            const enriched = await Promise.all(
                uploadedDocuments.map(async (doc) => {
                    const base64 = await readFileAsBase64(doc.file);
                    return {
                        ...doc,
                        base64,
                    };
                })
            );
            setLocalDocuments(enriched);
        };

        enrichDocuments();
    }, [uploadedDocuments]);

    const readFileAsBase64 = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });

    return (
        <div className="tw-space-y-4">
            <p className="tw-text-sm tw-text-gray-600">{description}</p>

            {isMobileOrTablet ? (
                <div className="tw-space-y-3">
                    <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3">
                        <button
                            type="button"
                            onClick={handleFileSelect}
                            className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-h-12 tw-px-4 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-shadow-sm tw-bg-white hover:tw-bg-gray-50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-blue-500 tw-transition-colors"
                        >
                            <Upload className="tw-w-4 tw-h-4" />
                            Upload from Files
                        </button>
                        <button
                            type="button"
                            onClick={handleCameraCapture}
                            className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-h-12 tw-px-4 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-shadow-sm tw-bg-white hover:tw-bg-gray-50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-blue-500 tw-transition-colors"
                        >
                            <Camera className="tw-w-4 tw-h-4" />
                            Scan with Camera
                        </button>
                    </div>

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple={allowMultiple}
                        onChange={(e) => handleFileUpload(e.target.files)}
                        className="tw-hidden"
                    />
                    <input
                        ref={cameraInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        multiple={allowMultiple}
                        onChange={(e) => handleFileUpload(e.target.files)}
                        className="tw-hidden"
                    />
                </div>
            ) : (
                <div className="tw-grid tw-grid-cols-1 tw-gap-4">
                    <div>
                        <button
                            type="button"
                            onClick={handleFileSelect}
                            className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-h-12 tw-px-4 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-shadow-sm tw-bg-white hover:tw-bg-gray-50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-blue-500 tw-transition-colors"
                        >
                            <Upload className="tw-w-4 tw-h-4" />
                            Upload from Computer
                        </button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            multiple={allowMultiple}
                            onChange={(e) => handleFileUpload(e.target.files, "desktop")}
                            className="tw-hidden"
                        />
                    </div>

                    <div className="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg tw-p-4">
                        <div className="tw-text-center">
                            <div className="tw-flex tw-items-center tw-justify-center tw-mb-2">
                                <Smartphone className="tw-w-4 tw-h-4 tw-text-blue-600 tw-mr-2" />
                                <span className="tw-text-sm tw-font-medium tw-text-blue-700">
                                    Mobile Upload
                                </span>
                            </div>
                            <p className="tw-text-sm tw-text-blue-600 tw-mb-3">
                                Scan this QR code with your phone to upload {documentType.toLowerCase()}
                            </p>
                            <div className="tw-flex tw-justify-center tw-mb-2">
                                <QRCodeGenerator value={uploadUrl} size={120} />
                            </div>
                            <p className="tw-text-xs tw-text-blue-600">
                                Open your phone's camera app and point at the QR code
                            </p>
                            <p className="tw-text-xs tw-text-gray-500 tw-mt-1 tw-break-all">
                                Session: {sessionId}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {localDocuments.length > 0 && (
                <div className="tw-space-y-2">
                    <p className="tw-text-sm tw-font-medium tw-text-gray-700">
                        Documents ({localDocuments.length}):
                    </p>
                    <div className="tw-space-y-2">
                        {localDocuments.map((doc, index) => (
                            <div
                                key={doc.id || index}
                                className="tw-flex tw-items-center tw-justify-between tw-rounded-md tw-p-3 tw-bg-green-50 tw-border tw-border-green-200"
                            >
                                <div className="tw-flex tw-items-center tw-gap-3">
                                    {doc.file.type.startsWith("image/") && doc.base64 && (
                                        <img
                                            src={doc.base64}
                                            alt={doc.file.name}
                                            className="tw-w-10 tw-h-10 tw-object-cover tw-rounded tw-border tw-border-green-300"
                                        />
                                    )}
                                    <div className="tw-flex tw-items-center tw-gap-2">
                                        {/* <FileImage className="tw-w-4 tw-h-4 tw-text-green-600" /> */}
                                        <div>
                                            <span className="tw-text-sm tw-text-green-700">{doc.file.name}</span>
                                            <div className="tw-text-xs tw-text-green-600">
                                                {(doc.file.size / 1024 / 1024).toFixed(2)} MB
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => removeFile(doc.id)}
                                    className="tw-text-green-600 hover:tw-text-green-800 tw-p-1"
                                >
                                    <X className="tw-w-4 tw-h-4" />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
